import * as React from 'react';
import { useState, useEffect } from 'react';
import { injectIntl, InjectedIntlProps, FormattedMessage, defineMessages } from 'react-intl';
import { History } from 'history';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import ReactGA from 'react-ga';

import { PAGE_SIZE, searchDatabase } from '../../services/search';
import logoImage from '../../images/logo-2.png';
import './styles.css';
import { SITE_URL, PAGE_PATH, EMBED_PATH, SEARCH_PATH } from '../../constants';
import { PropagateLoader, BarLoader } from 'react-spinners';
import { IDatabase, format, getAnalyticsToken, IDatabaseField } from '../../services/database';
import { getSepcialPageFieldKeysForDataabse } from 'src/helpers/pages';
import BraynResults from './BraynResults';
import CustomOneResults from './CustomOneResults';

export default injectIntl(
    (
        props: InjectedIntlProps & {
            history: History;
            database: IDatabase | null;
            databaseName: string;
            accountName: string;
        }
    ) => {
        const [results, setResults] = useState<any[]>([]);
        const [error, setError] = useState<boolean>(false);
        const [term, setTerm] = useState<string>('');
        const [pageId, setPageId] = useState<string>('');
        const [searchInput, setSearchInput] = useState<string>('');
        const [type, setType] = useState<string>(props?.database?.settings?.layout === 'brayn' ? 'map' : 'list');
        const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
        const [pageCount, setPageCount] = useState<number>(1);
        const [totalRowCount, setTotalRowCount] = useState<number>(0);
        const [loading, setLoading] = useState<boolean>(false);
        const [selectedFilters, setSelectedFilters] = useState<object>({});
        const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
        const [distance, setDistance] = useState<number | null>(10);
        const [currentDistance, setCurrentDistance] = useState<number | null>(10);
        const [selectedLocation, setSelectedLocation] = useState<{ lat: number, lng: number, address: string } | null>(null);
        const [currentLocation, setCurrentLocation] = useState<{ lat: number, lng: number, address: string } | null>(null);

        const addTracker = async (database: any) => {
            if (!database || !database.name) return;
            const code = await getAnalyticsToken(database.name);
            ReactGA.addTrackers([
                {
                    trackingId: code.replace(/"/g, ''),
                    gaOptions: { name: 'client' },
                },
            ]);
            ReactGA.pageview(`${database.title}`, ['harmonize', 'client']);
        };

        useEffect(() => {
            console.log(props);
            if (
                !props.history ||
                !props.history.location ||
                !props.history.location.state ||
                !props.history.location.state.code
            )
                addTracker(props.database);
            if (props?.database?.settings?.layout === 'brayn') setType('map')
        }, [props.database]);

        useEffect(() => {
            handleUrlChange();
        }, [props.history.location]);

        const messages = defineMessages({
            poweredBy: {
                id: 'public.search.results.hdr.poweredBy',
                defaultMessage: 'Powered By',
            },
            searchResults: {
                id: 'public.search.result.hdr.searchResults',
                defaultMessage: 'Search Results',
            },
            resultsFound: {
                id: 'public.search.result.hdr.resultsFound',
                defaultMessage: 'results found',
            },
            resultFound: {
                id: 'public.search.result.hdr.resultFound',
                defaultMessage: 'result found',
            },
            search: {
                id: 'public.search.result.hdr.search',
                defaultMessage: 'Search',
            },
            listView: {
                id: 'public.search.result.hdr.listView',
                defaultMessage: 'List View',
            },
            gridView: {
                id: 'public.search.result.hdr.gridView',
                defaultMessage: 'Grid View',
            },
        });

        const setFilter = (fieldName: string, val: string) => {
            if (val && val.length > 0) {
                const x = { ...selectedFilters };
                x[fieldName] = val;
                setSelectedFilters(x);
            } else {
                const x = { ...selectedFilters };
                delete x[fieldName];
                setSelectedFilters(x);
            }
        };

        const clearFilters = (clearLocation?: boolean) => {
            setSelectedFilters({});
            // if (document.getElementById('searchAddressBar')?.style?.display !== 'block') {
            if (clearLocation) {
                setSelectedLocation(null)
                setCurrentLocation(null)
                setDistance(10)
                search(searchInput, 1, '', {});
            } else search(searchInput, 1, '');
        };

        const buildUrl = (
            searchTerm: string,
            pageIndex: number = 1,
            locationOverride?: any,
            pageReload?: boolean
        ) => {
            let filterString = '';

            Object.entries(selectedFilters).forEach(
                (kvp) => kvp[0] && kvp[1] && `${kvp[1]}`.charAt(0) !== '~' && (filterString += `${filterString ? '&' : ''}${kvp[0]}=${Array.isArray(kvp[1]) ? kvp[1].map(encodeURIComponent).reduce((p, c) => p += `${p ? `&${kvp[0]}=` : ''}${c}`, '') : encodeURIComponent(`${kvp[1]}`)}`)
            );

            const {
                map: mapKey,
            }: any = props.database ? getSepcialPageFieldKeysForDataabse(props.database) : {};

            let q = ''
            const location = locationOverride === undefined ? selectedLocation || currentLocation : locationOverride
            if (searchTerm) q = `term=${searchTerm}`
            if (mapKey && location?.address) {
                const lat = location?.lat
                const lng = location?.lng
                q += `${q ? '&' : ''}lat=${lat}&lng=${lng}&address=${encodeURIComponent(location.address)}&distance=${distance || 1}&${mapKey}=~${lat},${lng},${Math.floor((distance || 1) * 1609.34)}`
            } 

            return `${SEARCH_PATH(
                props.databaseName,
                props.accountName
            )}?currentPage=${pageIndex}${q ? `&${q}` : ''}${filterString ? `&${filterString}` : ''}${type ? `&type=${type}` : ''}`;
        }

        const search = async (
            searchTerm: string,
            pageIndex: number = 1,
            filters: string | undefined = undefined,
            locationOverride?: any,
            withoutReload?: boolean
        ) => {
            if (filters === undefined) {
                if (withoutReload) props.history.push(buildUrl(searchTerm, pageIndex, locationOverride))
                else window.location.href = buildUrl(searchTerm, pageIndex, locationOverride)
            } else {
                setLoading(true);
                const result = await searchDatabase(
                    props.databaseName,
                    searchTerm,
                    filters || '',
                    pageIndex
                );
                const hits = [...result.data.rows];
                if (result.success) {
                    if (Array.isArray(result.data.rows)) {
                        setCurrentPageNumber(result.data.page.current);
                        setPageCount(Math.ceil(result.data.hitCount / PAGE_SIZE));
                        setTotalRowCount(result.data.hitCount);
                        setLoading(false);
                        setResults(hits);
                    }
                } else setError(true);
                setLoading(false);
            }
        };

        const getUrlForFilters = (filters) => {
            let filterString = ''
            Object.entries(filters).forEach(
                (kvp) => {
                    const value = kvp[1]
                    // Here we are going to generate the URL to be sent for the database, we want to encode all values except the location searcg which starts with `~`
                    if (kvp[0] && value)
                        (filterString += `${filterString ? '&' : ''}${kvp[0]}=${`${value}`.charAt(0) === '~' ? value : Array.isArray(kvp[1]) ? kvp[1].map(encodeURIComponent).join(',') : encodeURIComponent(`${value}`)}`)
                    // We are going to remove the location param from the filters as that is generated with every request
                    if (`${value}`.charAt(0) === '~') delete filters[kvp[0]]
                }
            );
            return filterString
        }

        const handleUrlChange = () => {
            const parsed: any = queryString.parse(window.location.search);
            parsed.term = parsed.term || '';

            // get filters by taking all query params minus term and page
            const filters = Object.assign({}, parsed);
            delete filters.term;
            delete filters.currentPage;
            delete filters.lat;
            delete filters.lng;
            delete filters.address;
            delete filters.distance;
            delete filters.type;
            delete filters.pageId;

            const filterString = getUrlForFilters(filters)

            setTerm(parsed.term);
            setPageId(parsed.pageId);
            setSelectedFilters(filters);
            setSearchInput(parsed.term);
            if (parsed.lat && parsed.lng && parsed.address)
                setCurrentLocation({ lat: parseFloat(parsed.lat), lng: parseFloat(parsed.lng), address: parsed.address })
            if (!Number.isNaN(parseInt(parsed.distance, 10))) {
                setCurrentDistance(parseFloat(parsed.distance))
                setDistance(parseFloat(parsed.distance));
            }
            if (parsed.type) setType(parsed.type)
            search(parsed.term, parsed.currentPage || 1, filterString);
        };

        const renderPoweredBy = () => {
            if (window.location.pathname.includes(EMBED_PATH)) {
                return (
                    <div className="poweredby">
                        <a href={SITE_URL} target="_blank">
                            <span>
                                <FormattedMessage
                                    id="search.form.poweredby"
                                    defaultMessage="Powered by"
                                />
                            </span>
                            <img src={logoImage} width="100" />
                        </a>
                    </div>
                );
            }
            return null;
        };

        const renderContactInfo = (item: any) => {
            if (item.Contact_Information_email) {
                try {
                    const email =
                        item.Contact_Information_email &&
                        JSON.parse(item.Contact_Information_email);
                    const firstName =
                        item.Contact_Information_first_name &&
                        JSON.parse(item.Contact_Information_first_name);
                    const lastName =
                        item.Contact_Information_last_name &&
                        JSON.parse(item.Contact_Information_last_name);
                    const name =
                        item.Contact_Information_name && JSON.parse(item.Contact_Information_name);
                    return (
                        <div>
                            {firstName || lastName ? (
                                <h3>
                                    {firstName} {lastName}
                                </h3>
                            ) : (
                                name && <h3>{name}</h3>
                            )}
                            <b>{email && <a href={`mailto:${email}`}>{email}</a>}</b>
                        </div>
                    );
                } catch (ex) { }
            }
            return null;
        };

        const renderSearchResultWrapper = (content: any) => {
            if (type === 'grid')
                return (
                    <div className="col-lg-4 col-sm-12 col-md-6">
                        <div className="liveviewCon">{content}</div>
                    </div>
                );
            return content;
        };

        const renderSearchResult = (item: any) => {
            try {

                if (!props.database) return
                const { title: titleKey, description: descKey }: any = getSepcialPageFieldKeysForDataabse(props.database)

                const title = item[titleKey] && JSON.parse(item[titleKey])
                const desciption = item[descKey] && JSON.parse(item[descKey])

                const image = item.account_organization_avatar;
                let bgImage = {};
                if (image !== '(none)') {
                    bgImage = {
                        backgroundImage: `url("${image.replace(/ /g, '%20')}")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: '300px 168.75px',
                    };
                }
                return renderSearchResultWrapper(
                    <Link
                        key={item.name}
                        style={{ textDecoration: 'none', color: 'black', cursor: 'pointer', minHeight: 250 }}
                        className="row py-2 px-3"
                        to={{
                            pathname: PAGE_PATH(item.name, item.account_name),
                            state: {
                                page: item,
                                database: props.database,
                            },
                        }}>
                        {image !== '(none)' && (
                            <div
                                className="col border-top border-bottom border-left p-0 image d-none d-md-block"
                                style={{ ...bgImage, overflow: 'hidden' }}>
                                {image ? (
                                    <span />
                                ) : (
                                    <div className="mx-auto">
                                        <BarLoader height={4} width={300} />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="col border p-3">
                            <h4>{item?.account_organization_name}</h4>
                            <Link
                                key={item.name}
                                to={{
                                    pathname: PAGE_PATH(item.name, item.account_name),
                                    state: {
                                        page: item,
                                        database: props.database,
                                    },
                                }}>
                                <h2>{title}</h2>
                            </Link>
                            <p className="small-text">{desciption}</p>
                            {renderContactInfo(item)}
                        </div>
                    </Link>
                );
            } catch (ex) { }
            return null;
        };

        const goToPage = (index: number) => {
            // TODO filters
            search(searchInput, index);
        };

        const renderPager = () => {
            if (pageCount > 1) {
                return (
                    <div className="paginationArea">
                        <ul>
                            {Array.from({ length: pageCount }, (x, i) => i).map((i) => (
                                <li key={`pager-${i}`}>
                                    <span
                                        className={i + 1 === currentPageNumber ? 'active' : ''}
                                        onClick={() => goToPage(i + 1)}>
                                        {i + 1}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            }
            return null;
        };

        const handleKeyDown = (e: any) => {
            if (e.key === 'Enter') {
                setTerm(searchInput);
                // TODO filters
                search(searchInput, 1);
            }
        };

        const renderFilters = () => {
            return (
                <React.Fragment>
                    <div className="col-lg-3 col-md-12 col-sm-12 border-top bg-light d-none d-lg-block">
                        <div className="p-4 pl-4 ">
                            <div className="py-3">
                                <b>Filters </b>
                                <button
                                    className="btn btn-link float-right p-0 text-harmonize-blue"
                                    onClick={() => clearFilters()}>
                                    {' '}
                                    Clear All
                                </button>
                            </div>
                            {renderFilterFields()}
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-12 col-sm-12 p-2 d-lg-none">
                        <div className="">
                            <div className="py-1">
                                <b>
                                    <button
                                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                                        className="btn btn-link p-0">
                                        Filters
                                        {Object.keys(selectedFilters).length
                                            ? ` (${Object.keys(selectedFilters).length}) `
                                            : ''}{' '}
                                        <i
                                            className={`fas fa-caret-${filtersExpanded ? 'up' : 'down'
                                                }`}
                                        />
                                    </button>
                                </b>
                                <button
                                    className="btn btn-link float-right p-0"
                                    onClick={() => clearFilters()}>
                                    {' '}
                                    Clear All
                                </button>
                            </div>
                            {filtersExpanded && renderFilterFields()}
                        </div>
                    </div>
                </React.Fragment>
            );
        };

        const renderFilterFields = () =>
            props.database &&
            props.database.filters.map((x) => {
                if (props.database) {
                    const field = props.database.fields.find((f) => format(f.group, f.name) === x);
                    if (!field) return null;
                    return (
                        <div key={field.name} className="form-group py-2">
                            <label htmlFor={field.name}>{field.label}: </label>
                            <select
                                id={field.name}
                                value={selectedFilters[x] || ''}
                                className="custom-select"
                                onChange={(event) => setFilter(x, event.target.value)}>
                                <option value="" key="0" />
                                {field.options.map((o, i) => (
                                    <option key={`${o}-${i}`} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                }
                return null;
            });

        if (props.database !== null && props?.database?.settings?.layout === 'customOne')
        return (
            <CustomOneResults
                database={props.database}
                results={results}
                intl={props.intl}
                term={term}
                pageId={pageId}
                setTerm={setTerm}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                setLocation={setSelectedLocation}
                selectedLocation={selectedLocation}
                currentLocation={currentLocation}
                selectedFilters={selectedFilters}
                setSelectedFilters={setFilter}
                currentDistance={currentDistance}
                distance={distance}
                setDistance={setDistance}
                onSearch={search}
                onClearFilters={clearFilters}
                loading={loading}
                renderPager={renderPager}
                totalRowCount={totalRowCount}
                type={type}
                setType={setType}
                onKeyDown={handleKeyDown}
            />
        )
        if (props.database !== null && props.database.settings.layout !== 'brayn')
        return (
            <div className="d-flex flex-row flex-fill responsive-padding">
                <div className="container-fluid">
                    <main role="main" id="content" tabIndex={-1}>
                        <div className="row">
                            <div className="col-lg-7 col-sm-12 col-md-7 searchPageTitle">
                                <div className="topsearch">
                                    <div className="searchform">
                                        <label className="sr-only" htmlFor="searchBar">
                                            {props.intl.formatMessage(messages.search)}
                                        </label>
                                        <input
                                            id="searchBar"
                                            value={searchInput}
                                            type="text"
                                            placeholder={props.intl.formatMessage(messages.search)}
                                            onChange={(e) => setSearchInput(e.currentTarget.value)}
                                            onKeyDown={handleKeyDown}
                                        />
                                        {/* TODO filters */}
                                        <a
                                            href="#"
                                            onClick={() => search(searchInput, 1)}>
                                            {props.intl.formatMessage(messages.search)}
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-5 col-sm-12 col-md-5 topoweredby">
                                {renderPoweredBy()}
                            </div>
                            <div className="col-lg-5 col-sm-12 col-md-12">
                                <div className="searchLtstep pl-4 py-2 d-none d-md-block">
                                    <h1 className="font-weight-light">
                                        {props.intl.formatMessage(messages.searchResults)}
                                        {term && `: ${term}`}
                                    </h1>
                                    <span>
                                        {totalRowCount}{' '}
                                        {totalRowCount === 1
                                            ? props.intl.formatMessage(messages.resultFound)
                                            : props.intl.formatMessage(messages.resultsFound)}
                                    </span>
                                </div>
                                <div className="searchLtstep d-md-none pt-3">
                                    <h1 className="font-weight-light">
                                        {props.intl.formatMessage(messages.searchResults)}
                                        {term && `: ${term}`}
                                    </h1>
                                    <span>
                                        {totalRowCount}{' '}
                                        {totalRowCount === 1
                                            ? props.intl.formatMessage(messages.resultFound)
                                            : props.intl.formatMessage(messages.resultsFound)}
                                    </span>
                                </div>
                            </div>
                            <div className="col-lg-7 col-sm-12 col-md-12">
                                {/* <div className="searchRtstep">
                            <ul className="nav s-navtabs" id="myTab" role="tablist">
                                <li className="nav-item" key="0">
                                    <a
                                        className={`nav-link ${type === 'list' && 'active'}`}
                                        data-toggle="tab" role="tab"
                                        aria-controls="listView"
                                        aria-selected="true"
                                        onClick={x => setType('list')}>{props.intl.formatMessage(messages.listView)}</a>
                                </li>
                                <li className="nav-item" key="1">
                                    <a className={`nav-link ${type === 'grid' && 'active'}`}
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="gridView"
                                        aria-selected="false"
                                        onClick={x => setType('grid')}>{props.intl.formatMessage(messages.gridView)}</a>
                                </li>
                                {/* <li className="nav-item">
                                    <a className="nav-link" id="mapView-tab" data-toggle="tab" href="#mapView" role="tab" aria-controls="mapView" aria-selected="false">Map View</a>
                                </li>
                            </ul>
                </div> */}
                            </div>
                            <div className="col-lg-12">
                                <div className="row">
                                    {renderFilters()}
                                    <div className="col-lg-9 col-md-12 col-sm-12 border-top">
                                        <div
                                            className="tab-content searchfltercontent pt-4"
                                            id="myTabContent">
                                            <div
                                                className="tab-pane fade show active"
                                                id="listView"
                                                role="tabpanel">
                                                {loading && (
                                                    <div className="p-5 d-flex justify-content-center">
                                                        <PropagateLoader
                                                            size={15}
                                                            loading={loading}
                                                            color="#29447b"
                                                        />
                                                    </div>
                                                )}
                                                {results.map(renderSearchResult)}
                                            </div>
                                            {renderPager()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
        return (
            <BraynResults
                database={props.database}
                results={results}
                intl={props.intl}
                term={term}
                pageId={pageId}
                setTerm={setTerm}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                setLocation={setSelectedLocation}
                selectedLocation={selectedLocation}
                currentLocation={currentLocation}
                selectedFilters={selectedFilters}
                setSelectedFilters={setFilter}
                currentDistance={currentDistance}
                distance={distance}
                setDistance={setDistance}
                onSearch={search}
                onClearFilters={clearFilters}
                loading={loading}
                renderPager={renderPager}
                totalRowCount={totalRowCount}
                type={type}
                setType={setType}
                onKeyDown={handleKeyDown}
            />
        )
    }
);



// WEBPACK FOOTER //
// ./src/public/search/Results.tsx